import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, CATEGORY_COLORS, LOCATIONS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PageHeader from "@/components/PageHeader";
import StockMovementDialog from "@/components/StockMovementDialog";
import { Button } from "@/components/ui/button";
import {
    Wallet,
    Package,
    AlertTriangle,
    ArrowDownRight,
    ArrowUpRight,
    Boxes,
    Plus,
    Minus,
    KeyRound,
    RefreshCw,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [openIn, setOpenIn] = useState(false);
    const [openOut, setOpenOut] = useState(false);

    const load = async () => {
        const [d, c, p] = await Promise.all([
            api.get("/dashboard"),
            api.get("/categories"),
            api.get("/products"),
        ]);
        setData(d.data);
        setCategories(c.data);
        setProducts(p.data);
    };

    useEffect(() => {
        load();
    }, []);

    const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));

    const renewRental = async (r) => {
        try {
            await api.post(`/rentals/${r.id}/renew`);
            toast.success(`"${r.name}" renouvelée`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try {
            return format(parseISO(iso), "d MMM yyyy", { locale: fr });
        } catch {
            return iso;
        }
    };

    const rentalDaysLabel = (d) => {
        if (d === null || d === undefined) return "—";
        if (d < 0) return `expirée depuis ${Math.abs(d)} j`;
        if (d === 0) return "expire aujourd'hui";
        if (d === 1) return "expire demain";
        return `${d} j restants`;
    };

    return (
        <>
            <PageHeader
                title="Tableau de bord"
                subtitle="Vue d'ensemble de votre coffre et de votre stock"
                actions={
                    <>
                        <Button
                            data-testid="dashboard-new-in-btn"
                            onClick={() => setOpenIn(true)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Entrée stock
                        </Button>
                        <Button
                            data-testid="dashboard-new-out-btn"
                            onClick={() => setOpenOut(true)}
                            variant="outline"
                            className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                        >
                            <Minus className="w-4 h-4 mr-2" />
                            Sortie stock
                        </Button>
                    </>
                }
            />

            <div className="p-8 space-y-8">
                {/* Metric Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card
                        data-testid="metric-vault"
                        className="border-slate-200 shadow-sm p-6 bg-white"
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Argent en coffre
                                </div>
                                <div className="font-display text-3xl font-bold tracking-tighter text-slate-900 mt-3 tabular-nums">
                                    {formatMoney(data?.vault_total)}
                                </div>
                                <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3">
                                    {LOCATIONS.map((loc) => (
                                        <div
                                            key={loc.key}
                                            className="flex items-center justify-between text-sm"
                                            data-testid={`vault-sub-${loc.key}`}
                                        >
                                            <span className="text-slate-500">
                                                {loc.label}
                                            </span>
                                            <span className="font-medium tabular-nums text-slate-900">
                                                {formatMoney(
                                                    data?.vault_balances?.[loc.key]
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="w-10 h-10 rounded-md flex items-center justify-center text-[#7A6018] bg-[#F0E5C7] ml-3 shrink-0">
                                <Wallet className="w-5 h-5" />
                            </div>
                        </div>
                    </Card>
                    <MetricCard
                        testid="metric-stock-value"
                        label="Valeur totale du stock"
                        value={formatMoney(data?.total_stock_value)}
                        icon={<Boxes className="w-5 h-5" />}
                        hint={`${data?.total_items ?? 0} articles · ${data?.total_products ?? 0} produits`}
                    />
                    <MetricCard
                        testid="metric-low-stock"
                        label="Alertes stock bas"
                        value={data?.low_stock_count ?? 0}
                        icon={<AlertTriangle className="w-5 h-5" />}
                        accent={data?.low_stock_count > 0 ? "rose" : "neutral"}
                    />
                </div>

                {/* Rental alerts */}
                <Card
                    data-testid="dashboard-rentals-alerts"
                    className="border-slate-200 shadow-sm"
                >
                    <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Rappels
                            </div>
                            <h2 className="font-display text-xl font-semibold text-slate-900 mt-0.5">
                                Locations à renouveler
                            </h2>
                        </div>
                        <div className="flex items-center gap-3">
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
                                {data?.rentals_alerts_count ?? 0} rappel(s)
                            </Badge>
                            <Link to="/rentals">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="dashboard-rentals-view-all"
                                    className="text-slate-600 hover:text-slate-900"
                                >
                                    Voir tout
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {(data?.rentals_alerts || []).length === 0 && (
                            <div className="px-6 py-8 text-sm text-slate-500 text-center">
                                Aucune location à renouveler pour le moment.
                            </div>
                        )}
                        {(data?.rentals_alerts || []).map((r) => {
                            const isExpired = r.status === "expired";
                            return (
                                <div
                                    key={r.id}
                                    data-testid={`dashboard-rental-alert-${r.id}`}
                                    className="px-6 py-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div
                                            className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
                                                isExpired
                                                    ? "bg-rose-100 text-rose-700"
                                                    : "bg-amber-100 text-amber-700"
                                            }`}
                                        >
                                            <KeyRound className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-medium text-slate-900 truncate">
                                                {r.name}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                Fin le {fmtDate(r.end_date)}
                                                {r.cost > 0 && (
                                                    <>
                                                        {" · Coût "}
                                                        {formatMoney(r.cost)}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <Badge
                                            className={
                                                isExpired
                                                    ? "bg-rose-100 text-rose-800 border-rose-200"
                                                    : "bg-amber-100 text-amber-800 border-amber-200"
                                            }
                                        >
                                            {rentalDaysLabel(r.days_left)}
                                        </Badge>
                                        <Button
                                            size="sm"
                                            onClick={() => renewRental(r)}
                                            data-testid={`dashboard-rental-renew-${r.id}`}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                            <RefreshCw className="w-4 h-4 mr-1.5" />
                                            Renouveler
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>

                {/* Low stock alerts */}
                <Card className="border-slate-200 shadow-sm">
                    <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Alertes
                            </div>
                            <h2 className="font-display text-xl font-semibold text-slate-900 mt-0.5">
                                Produits en stock bas
                            </h2>
                        </div>
                        <Badge className="bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-100">
                            {data?.low_stock_count ?? 0} alerte(s)
                        </Badge>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {(data?.low_stock_products || []).length === 0 && (
                            <div className="px-6 py-8 text-sm text-slate-500 text-center">
                                Aucune alerte. Tous les stocks sont au-dessus du seuil.
                            </div>
                        )}
                        {(data?.low_stock_products || []).map((p) => {
                            const cat = catMap[p.category_id];
                            const color =
                                CATEGORY_COLORS[cat?.color] || CATEGORY_COLORS.slate;
                            return (
                                <div
                                    key={p.id}
                                    className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Package className="w-4 h-4 text-slate-400 shrink-0" />
                                        <div className="min-w-0">
                                            <div className="font-medium text-slate-900 truncate">
                                                {p.name}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                Seuil : {p.low_stock_threshold} · Achat{" "}
                                                {formatMoney(p.purchase_price)} · Vente{" "}
                                                {formatMoney(p.sale_price)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {cat && (
                                            <Badge
                                                variant="outline"
                                                className={`${color} border`}
                                            >
                                                {cat.name}
                                            </Badge>
                                        )}
                                        <span className="text-lg font-semibold text-rose-700 tabular-nums">
                                            {p.quantity}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>

                {/* Recent transactions */}
                <Card className="border-slate-200 shadow-sm">
                    <div className="px-6 py-5 border-b border-slate-200">
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Activité
                        </div>
                        <h2 className="font-display text-xl font-semibold text-slate-900 mt-0.5">
                            Transactions récentes
                        </h2>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {(data?.recent_transactions || []).length === 0 && (
                            <div className="px-6 py-8 text-sm text-slate-500 text-center">
                                Aucune transaction pour le moment.
                            </div>
                        )}
                        {(data?.recent_transactions || []).map((tx) => (
                            <div
                                key={tx.id}
                                className="px-6 py-3.5 flex items-center justify-between hover:bg-slate-50"
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`w-8 h-8 rounded-md flex items-center justify-center ${
                                            tx.type === "IN"
                                                ? "bg-emerald-100 text-emerald-700"
                                                : "bg-rose-100 text-rose-700"
                                        }`}
                                    >
                                        {tx.type === "IN" ? (
                                            <ArrowDownRight className="w-4 h-4" />
                                        ) : (
                                            <ArrowUpRight className="w-4 h-4" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-medium text-slate-900 text-sm">
                                            {tx.product_name}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            {format(new Date(tx.created_at), "dd/MM/yyyy HH:mm")}
                                            {tx.note ? ` · ${tx.note}` : ""}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div
                                        className={`font-semibold tabular-nums ${
                                            tx.type === "IN"
                                                ? "text-emerald-700"
                                                : "text-rose-700"
                                        }`}
                                    >
                                        {tx.type === "IN" ? "+" : "−"}
                                        {tx.quantity} × {formatMoney(tx.unit_price)}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Total {formatMoney(tx.total)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <StockMovementDialog
                open={openIn}
                onOpenChange={setOpenIn}
                type="IN"
                products={products}
                onDone={load}
            />
            <StockMovementDialog
                open={openOut}
                onOpenChange={setOpenOut}
                type="OUT"
                products={products}
                onDone={load}
            />
        </>
    );
}

function MetricCard({ label, value, icon, accent = "neutral", hint, testid }) {
    const accentClasses =
        accent === "emerald"
            ? "text-emerald-700 bg-emerald-100"
            : accent === "rose"
              ? "text-rose-700 bg-rose-100"
              : "text-slate-700 bg-slate-100";
    return (
        <Card
            data-testid={testid}
            className="border-slate-200 shadow-sm p-6 bg-white"
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                        {label}
                    </div>
                    <div className="font-display text-4xl font-bold tracking-tighter text-slate-900 mt-3 tabular-nums">
                        {value}
                    </div>
                    {hint && (
                        <div className="text-xs text-slate-500 mt-2">{hint}</div>
                    )}
                </div>
                <div
                    className={`w-10 h-10 rounded-md flex items-center justify-center ${accentClasses}`}
                >
                    {icon}
                </div>
            </div>
        </Card>
    );
}
